import { startWith, map, takeUntil, filter, first, share, distinctUntilChanged, switchMap, shareReplay, tap } from 'rxjs/operators';
import { Directive, Injectable, EventEmitter, Inject, Output, Input, Component, ChangeDetectionStrategy, NgModule, Self, ChangeDetectorRef, SkipSelf, Optional, ContentChildren } from '@angular/core';
import { NgControl, ControlContainer } from '@angular/forms';
import { Subject, of, isObservable, BehaviorSubject, combineLatest } from 'rxjs';
import { CommonModule } from '@angular/common';

// @dynamic
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@angular/forms';
import * as ɵngcc2 from '@angular/common';
class FmxFormUtils {
    /**
     * Add validators to a control without removing the existing ones
     */
    static addValidatorsToControl(control, newValidator) {
        if (control) {
            const newValidators = Array.isArray(newValidator) ? newValidator : [newValidator];
            const validators = control.validator ? [control.validator, ...newValidators] : newValidators;
            control.setValidators(validators);
        }
    }
    /**
     * Add async validators to a control without removing the existing ones
     */
    static addAsyncValidatorsToControl(control, newValidator) {
        if (control) {
            const newValidators = Array.isArray(newValidator) ? newValidator : [newValidator];
            const validators = control.asyncValidator ? [control.asyncValidator, ...newValidators] : newValidators;
            control.setAsyncValidators(validators);
        }
    }
    /**
     * Get control's errors as an Observable
     * Main use case: Changes done by async validators into ${@link AbstractControl.errors}
     * are not spotted when using ${@link ChangeDetectionStrategy.OnPush}
     */
    static getControlErrors$(control) {
        return control.statusChanges.pipe(startWith(''), map(() => control.errors));
    }
}

/**
 * @typeParam I type of control's input value, used as type param for {@link writeValue}
 * @typeParam O type of control's output value, used as type param for the changeFn received through ${@link registerOnChange} and as
 * return type for {@link mapToOutput}
 */
// tslint:disable-next-line:directive-class-suffix
class ControlValueAccessor {
    constructor(ngControl) {
        this.ngControl = ngControl;
        this.destroyed$ = new Subject();
        this.isDestroyed = false;
        ngControl.valueAccessor = this;
    }
    get destroyed() {
        return this.isDestroyed;
    }
    writeValue(value) {
        this.control.reset(value);
    }
    registerOnChange(fn) {
        this.valueChanges$().pipe(takeUntil(this.destroyed$), map(value => this.mapToOutput(value))).subscribe(fn);
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    ngOnInit() {
        this.errors$ = this.getErrors$(this.control);
        this.externalControl = this.ngControl.control;
        this.externalErrors$ = this.getErrors$(this.externalControl);
        // angular bug, setValidators() in nested form control
        // https://github.com/angular/angular/issues/18004, https://github.com/angular/angular/issues/23657
        setTimeout(() => this.registerExternalValidators(), 0);
    }
    ngOnDestroy() {
        this.isDestroyed = true;
        this.destroyed$.next();
    }
    valueChanges$() {
        return this.control.valueChanges;
    }
    mapToOutput(value) {
        return value;
    }
    registerExternalValidators() {
        FmxFormUtils.addValidatorsToControl(this.externalControl, this.validatorFn());
        FmxFormUtils.addAsyncValidatorsToControl(this.externalControl, this.asyncValidatorFn());
        this.externalControl.updateValueAndValidity({ emitEvent: false });
    }
    validatorFn() {
        return (control) => {
            var _a;
            if (this.destroyed) {
                /** If this control is destroyed, but the ${@link externalControl} is not, the validators set by {@link registerExternalValidators}
                 * will remain active. So by returning null when this control is destroyed it will not continue to affect
                 * {@link externalControl}s validation status.
                 *
                 * This is a workaround until Angular provides a way to remove a specific validator.
                 * Right now, on version 10.0.11, all validators are composed under one validation function.
                 */
                return null;
            }
            if (this.control.invalid) {
                return (_a = this.control.errors) !== null && _a !== void 0 ? _a : { invalid: true };
            }
            return null;
        };
    }
    asyncValidatorFn() {
        return (control) => {
            return this.control.statusChanges.pipe(
            /** @see validatorFn explanation regarding the use of {@link destroyed} in the validation logic */
            startWith(!this.destroyed ? this.control.status : 'ACTIVE'), filter(status => status !== 'PENDING'), // if async validation does not emit a value it's recalled until it does
            map(status => { var _a; return status === 'INVALID' ? ((_a = this.control.errors) !== null && _a !== void 0 ? _a : { invalid: true }) : null; }), first());
        };
    }
    markAsTouched() {
        this.control.markAsTouched();
        this.externalControl.markAsTouched();
    }
    markAsDirty() {
        this.control.markAsDirty();
        this.externalControl.markAsDirty();
    }
    getErrors$(control) {
        return FmxFormUtils.getControlErrors$(control).pipe(takeUntil(this.destroyed$), share());
    }
}
ControlValueAccessor.ɵfac = function ControlValueAccessor_Factory(t) { return new (t || ControlValueAccessor)(ɵngcc0.ɵɵdirectiveInject(ɵngcc1.NgControl)); };
ControlValueAccessor.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: ControlValueAccessor });
ControlValueAccessor.ctorParameters = () => [
    { type: NgControl }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ControlValueAccessor, [{
        type: Directive
    }], function () { return [{ type: ɵngcc1.NgControl }]; }, null); })();

class FmxErrorsService {
    constructor() {
        /**
         * Map of globally registered error message resolvers
         */
        this.resolvers = new Map();
    }
    registerResolver(resolver, overwrite = false) {
        if (this.resolvers.has(resolver.errorKey) && !overwrite) {
            throw new Error(`Error message resolver for key: ${resolver.errorKey} is already registered! ` +
                `If you wish to overwrite it, please set the 'overwrite' flag to true`);
        }
        this.resolvers.set(resolver.errorKey, resolver);
    }
    registerResolvers(resolvers) {
        resolvers === null || resolvers === void 0 ? void 0 : resolvers.forEach(resolver => this.registerResolver(resolver));
    }
    removeResolver(resolver) {
        this.removeResolverByKey(resolver.errorKey);
    }
    removeResolvers(resolvers) {
        resolvers === null || resolvers === void 0 ? void 0 : resolvers.forEach(resolver => this.removeResolver(resolver));
    }
    removeResolverByKey(errorKey) {
        this.resolvers.delete(errorKey);
    }
    removeResolversByKey(errorKeys) {
        errorKeys === null || errorKeys === void 0 ? void 0 : errorKeys.forEach(errorKey => this.removeResolverByKey(errorKey));
    }
    getErrorMessage(errors, options) {
        if (errors === null) {
            return of(null);
        }
        const controlName = (options === null || options === void 0 ? void 0 : options.controlLabel) || 'This field';
        const controlResolvers = FmxErrorsService.toResolversMap((options === null || options === void 0 ? void 0 : options.resolvers) || []);
        const firstErrorKey = Object.keys(errors)[0];
        const resolver = controlResolvers.get(firstErrorKey) || this.resolvers.get(firstErrorKey);
        if (!resolver) {
            throw new Error(`No error resolver registered for error key: ${firstErrorKey}`);
        }
        const message = resolver.getMessage(errors[firstErrorKey], controlName);
        const message$ = isObservable(message) ? message : of(message);
        return message$.pipe(first());
    }
    static toResolversMap(resolvers) {
        const map = new Map(resolvers.map(resolver => [resolver.errorKey, resolver]));
        return map;
    }
}
FmxErrorsService.ɵfac = function FmxErrorsService_Factory(t) { return new (t || FmxErrorsService)(); };
FmxErrorsService.ɵprov = ɵngcc0.ɵɵdefineInjectable({ token: FmxErrorsService, factory: FmxErrorsService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FmxErrorsService, [{
        type: Injectable
    }], function () { return []; }, null); })();

class FmxErrorsDirective {
    constructor(errorService) {
        this.errorService = errorService;
        this.messageChange = new EventEmitter();
        this.destroyed$ = new Subject();
        this.errorsSubject = new BehaviorSubject(null);
        this.controlSubject = new BehaviorSubject(null);
        this.controlLabelSubject = new BehaviorSubject('');
        this.resolversSubject = new BehaviorSubject([]);
    }
    set errors(value) { this.errorsSubject.next(value); }
    set control(value) { this.controlSubject.next(value); }
    set controlLabel(value) { this.controlLabelSubject.next(value); }
    set visibility(value) { }
    set resolvers(value) { this.resolversSubject.next(value); }
    ngOnInit() {
        this.message$ = combineLatest([this.controlSubject.asObservable(), this.errorsSubject.asObservable()])
            .pipe(distinctUntilChanged(), takeUntil(this.destroyed$), map(([control, errors]) => control ? FmxFormUtils.getControlErrors$(control) : of(errors)), switchMap(errors$ => combineLatest([errors$, this.controlLabelSubject.asObservable(), this.resolversSubject.asObservable()])), switchMap(([errors, controlLabel, resolvers]) => this.errorService.getErrorMessage(errors, { controlLabel, resolvers })), shareReplay(1));
        this.message$.subscribe(message => this.messageChange.emit(message));
    }
    ngOnDestroy() {
        this.destroyed$.next();
    }
}
FmxErrorsDirective.ɵfac = function FmxErrorsDirective_Factory(t) { return new (t || FmxErrorsDirective)(ɵngcc0.ɵɵdirectiveInject(FmxErrorsService)); };
FmxErrorsDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: FmxErrorsDirective, selectors: [["", "fmxErrors", ""]], inputs: { errors: ["fmxErrors", "errors"], control: ["fmxErrorsControl", "control"], controlLabel: ["fmxErrorsControlLabel", "controlLabel"], visibility: ["fmxErrorsVisibility", "visibility"], resolvers: ["fmxErrorsResolvers", "resolvers"] }, outputs: { messageChange: "fmxErrorsMessage" }, exportAs: ["fmxErrors"] });
FmxErrorsDirective.ctorParameters = () => [
    { type: FmxErrorsService, decorators: [{ type: Inject, args: [FmxErrorsService,] }] }
];
FmxErrorsDirective.propDecorators = {
    messageChange: [{ type: Output, args: ['fmxErrorsMessage',] }],
    errors: [{ type: Input, args: ['fmxErrors',] }],
    control: [{ type: Input, args: ['fmxErrorsControl',] }],
    controlLabel: [{ type: Input, args: ['fmxErrorsControlLabel',] }],
    visibility: [{ type: Input, args: ['fmxErrorsVisibility',] }],
    resolvers: [{ type: Input, args: ['fmxErrorsResolvers',] }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FmxErrorsDirective, [{
        type: Directive,
        args: [{
                selector: '[fmxErrors]',
                exportAs: 'fmxErrors'
            }]
    }], function () { return [{ type: FmxErrorsService, decorators: [{
                type: Inject,
                args: [FmxErrorsService]
            }] }]; }, { messageChange: [{
            type: Output,
            args: ['fmxErrorsMessage']
        }], errors: [{
            type: Input,
            args: ['fmxErrors']
        }], control: [{
            type: Input,
            args: ['fmxErrorsControl']
        }], controlLabel: [{
            type: Input,
            args: ['fmxErrorsControlLabel']
        }], visibility: [{
            type: Input,
            args: ['fmxErrorsVisibility']
        }], resolvers: [{
            type: Input,
            args: ['fmxErrorsResolvers']
        }] }); })();

class FmxErrorsComponent {
    constructor() {
        this.errors = null;
        this.control = null;
        this.controlLabel = '';
        this.resolvers = [];
    }
}
FmxErrorsComponent.ɵfac = function FmxErrorsComponent_Factory(t) { return new (t || FmxErrorsComponent)(); };
FmxErrorsComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: FmxErrorsComponent, selectors: [["fmx-errors"]], inputs: { errors: "errors", control: "control", controlLabel: "controlLabel", resolvers: "resolvers" }, decls: 6, vars: 7, consts: [[3, "fmxErrors", "fmxErrorsControl", "fmxErrorsControlLabel", "fmxErrorsResolvers"], ["errorsDirective", "fmxErrors"]], template: function FmxErrorsComponent_Template(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵtext(0, "\n    ");
        ɵngcc0.ɵɵelementContainerStart(1, 0, 1);
        ɵngcc0.ɵɵtext(3);
        ɵngcc0.ɵɵpipe(4, "async");
        ɵngcc0.ɵɵelementContainerEnd();
        ɵngcc0.ɵɵtext(5, "\n  ");
    } if (rf & 2) {
        const _r0 = ɵngcc0.ɵɵreference(2);
        ɵngcc0.ɵɵadvance(1);
        ɵngcc0.ɵɵproperty("fmxErrors", ctx.errors)("fmxErrorsControl", ctx.control)("fmxErrorsControlLabel", ctx.controlLabel)("fmxErrorsResolvers", ctx.resolvers);
        ɵngcc0.ɵɵadvance(2);
        ɵngcc0.ɵɵtextInterpolate1("\n      ", ɵngcc0.ɵɵpipeBind1(4, 5, _r0.message$), "\n    ");
    } }, directives: [FmxErrorsDirective], pipes: [ɵngcc2.AsyncPipe], encapsulation: 2, changeDetection: 0 });
FmxErrorsComponent.propDecorators = {
    errors: [{ type: Input }],
    control: [{ type: Input }],
    controlLabel: [{ type: Input }],
    resolvers: [{ type: Input }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FmxErrorsComponent, [{
        type: Component,
        args: [{
                selector: 'fmx-errors',
                template: `
    <ng-container [fmxErrors]="errors"
                  [fmxErrorsControl]="control"
                  [fmxErrorsControlLabel]="controlLabel"
                  [fmxErrorsResolvers]="resolvers"
                  #errorsDirective='fmxErrors'>
      {{errorsDirective.message$ | async}}
    </ng-container>
  `,
                changeDetection: ChangeDetectionStrategy.OnPush
            }]
    }], function () { return []; }, { errors: [{
            type: Input
        }], control: [{
            type: Input
        }], controlLabel: [{
            type: Input
        }], resolvers: [{
            type: Input
        }] }); })();

class FmxErrorsModule {
    static forRoot() {
        return {
            ngModule: FmxErrorsModule,
            providers: [
                FmxErrorsService,
            ]
        };
    }
}
FmxErrorsModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: FmxErrorsModule });
FmxErrorsModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function FmxErrorsModule_Factory(t) { return new (t || FmxErrorsModule)(); }, imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(FmxErrorsModule, { declarations: function () { return [FmxErrorsComponent, FmxErrorsDirective]; }, imports: function () { return [CommonModule]; }, exports: function () { return [FmxErrorsComponent, FmxErrorsDirective]; } }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FmxErrorsModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                declarations: [
                    FmxErrorsComponent,
                    FmxErrorsDirective
                ],
                exports: [
                    FmxErrorsComponent,
                    FmxErrorsDirective
                ]
            }]
    }], null, null); })();

class RequiredErrorResolver {
    constructor() {
        this.errorKey = 'required';
    }
    getMessage(error, name) {
        return `${name} is required!`;
    }
}
class MinLengthErrorResolver {
    constructor() {
        this.errorKey = 'minlength';
    }
    getMessage(error, controlName) {
        return `${controlName} has to be min ${error.requiredLength} characters long`;
    }
}
class MaxLengthErrorResolver {
    constructor() {
        this.errorKey = 'maxlength';
    }
    getMessage(error, controlName) {
        return `${controlName} has to be max ${error.requiredLength} characters long`;
    }
}
class EmailErrorResolver {
    constructor() {
        this.errorKey = 'email';
    }
    getMessage(error, controlName) {
        return 'Please enter a valid email';
    }
}
const DEFAULT_ERROR_RESOLVERS = [
    new RequiredErrorResolver(),
    new MinLengthErrorResolver(),
    new MaxLengthErrorResolver(),
    new EmailErrorResolver()
];

class FmxControlSubmitDirective {
    constructor(ngControl, cdf) {
        this.ngControl = ngControl;
        this.cdf = cdf;
        /**
         * This will be called everytime the form is submitted
         */
        this.onControlSubmit = this.onSubmit;
        this.destroyed$ = new Subject();
    }
    get onSubmit() {
        return (control) => {
            control.markAsTouched();
            control.markAsDirty();
            this.cdf.markForCheck();
        };
    }
    ngOnInit() {
        const control = this.ngControl.control;
        this.submitValid$ = of(null)
            .pipe(takeUntil(this.destroyed$), tap(() => this.onControlSubmit(control)), switchMap(() => control.statusChanges
            .pipe(startWith(control.status), filter(status => status !== 'PENDING'), // wait before any async validators finish
        first())), map(status => status === 'VALID'));
    }
    ngOnDestroy() {
        this.destroyed$.next();
    }
}
FmxControlSubmitDirective.ɵfac = function FmxControlSubmitDirective_Factory(t) { return new (t || FmxControlSubmitDirective)(ɵngcc0.ɵɵdirectiveInject(NgControl, 2), ɵngcc0.ɵɵdirectiveInject(ChangeDetectorRef)); };
FmxControlSubmitDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: FmxControlSubmitDirective, selectors: [["", "formControl", ""], ["", "formControlName", ""]], inputs: { onControlSubmit: ["fmxOnControlSubmit", "onControlSubmit"] } });
FmxControlSubmitDirective.ctorParameters = () => [
    { type: NgControl, decorators: [{ type: Inject, args: [NgControl,] }, { type: Self }] },
    { type: ChangeDetectorRef, decorators: [{ type: Inject, args: [ChangeDetectorRef,] }] }
];
FmxControlSubmitDirective.propDecorators = {
    onControlSubmit: [{ type: Input, args: ['fmxOnControlSubmit',] }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FmxControlSubmitDirective, [{
        type: Directive,
        args: [{ selector: '[formControl], [formControlName]' }]
    }], function () { return [{ type: ɵngcc1.NgControl, decorators: [{
                type: Inject,
                args: [NgControl]
            }, {
                type: Self
            }] }, { type: ɵngcc0.ChangeDetectorRef, decorators: [{
                type: Inject,
                args: [ChangeDetectorRef]
            }] }]; }, { onControlSubmit: [{
            type: Input,
            args: ['fmxOnControlSubmit']
        }] }); })();

class FmxFormSubmitDirective {
    constructor(parent, controlContainer) {
        this.parent = parent;
        this.controlContainer = controlContainer;
        /**
         * Emits everytime the form is submitted.
         * Compared to ${@link FormGroupDirective.ngSubmit} and ${@link NgForm.ngSubmit} this doesn't emit until all
         * async validators have finished
         */
        this.fmxSubmit = new EventEmitter();
        this.controlDirectives = new Set();
        this.destroyed$ = new Subject();
    }
    /**
     * Collects all descendants of type {@link FmxControlSubmitDirective} and add them to the root form
     * @param controlDirectives
     * @private
     */
    set childrenDirectives(controlDirectives) {
        this.controlDirectives.clear();
        controlDirectives.forEach(directive => this.controlDirectives.add(directive));
        this.addControlDirectivesToRoot(Array.from(this.controlDirectives));
    }
    ngOnInit() {
        if (this.parent) {
            this.submitValid$ = this.parent.submitValid$;
        }
        else {
            this.submitValid$ = this.controlContainer.ngSubmit
                .pipe(takeUntil(this.destroyed$), map(() => Array.from(this.controlDirectives).map(directive => directive.submitValid$)), switchMap(submits$ => combineLatest([...submits$])), map((valids) => !valids.includes(false)), share());
            this.submitValid$.subscribe(value => this.fmxSubmit.emit(value));
        }
    }
    ngOnDestroy() {
        this.destroyed$.next();
    }
    /**
     * Trigger a form submit manually
     */
    submit() {
        this.controlContainer.onSubmit(new Event('Manually triggered submit'));
    }
    addControlDirectivesToRoot(directives) {
        if (this.parent) {
            this.parent.addControlDirectivesToRoot(directives);
        }
        else {
            directives.forEach(directive => this.controlDirectives.add(directive));
        }
    }
}
FmxFormSubmitDirective.ɵfac = function FmxFormSubmitDirective_Factory(t) { return new (t || FmxFormSubmitDirective)(ɵngcc0.ɵɵdirectiveInject(FmxFormSubmitDirective, 12), ɵngcc0.ɵɵdirectiveInject(ControlContainer, 10)); };
FmxFormSubmitDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: FmxFormSubmitDirective, selectors: [["", "formGroup", ""], ["", "formGroupName", ""]], contentQueries: function FmxFormSubmitDirective_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        ɵngcc0.ɵɵcontentQuery(dirIndex, FmxControlSubmitDirective, 1);
    } if (rf & 2) {
        let _t;
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.childrenDirectives = _t);
    } }, outputs: { fmxSubmit: "fmxSubmit" }, exportAs: ["fmxFormSubmit"] });
FmxFormSubmitDirective.ctorParameters = () => [
    { type: FmxFormSubmitDirective, decorators: [{ type: Inject, args: [FmxFormSubmitDirective,] }, { type: SkipSelf }, { type: Optional }] },
    { type: undefined, decorators: [{ type: Inject, args: [ControlContainer,] }, { type: Self }, { type: Optional }] }
];
FmxFormSubmitDirective.propDecorators = {
    fmxSubmit: [{ type: Output }],
    childrenDirectives: [{ type: ContentChildren, args: [FmxControlSubmitDirective, { descendants: true },] }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FmxFormSubmitDirective, [{
        type: Directive,
        args: [{ selector: '[formGroup], [formGroupName]', exportAs: 'fmxFormSubmit' }]
    }], function () { return [{ type: FmxFormSubmitDirective, decorators: [{
                type: Inject,
                args: [FmxFormSubmitDirective]
            }, {
                type: SkipSelf
            }, {
                type: Optional
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [ControlContainer]
            }, {
                type: Self
            }, {
                type: Optional
            }] }]; }, { fmxSubmit: [{
            type: Output
        }], childrenDirectives: [{
            type: ContentChildren,
            args: [FmxControlSubmitDirective, { descendants: true }]
        }] }); })();

class FmxSubmitModule {
}
FmxSubmitModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: FmxSubmitModule });
FmxSubmitModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function FmxSubmitModule_Factory(t) { return new (t || FmxSubmitModule)(); } });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(FmxSubmitModule, { declarations: [FmxFormSubmitDirective, FmxControlSubmitDirective], exports: [FmxFormSubmitDirective, FmxControlSubmitDirective] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(FmxSubmitModule, [{
        type: NgModule,
        args: [{
                declarations: [
                    FmxFormSubmitDirective,
                    FmxControlSubmitDirective
                ],
                exports: [
                    FmxFormSubmitDirective,
                    FmxControlSubmitDirective
                ]
            }]
    }], null, null); })();

/*
 * Public API Surface of ngx-formx
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ControlValueAccessor, DEFAULT_ERROR_RESOLVERS, EmailErrorResolver, FmxControlSubmitDirective, FmxErrorsComponent, FmxErrorsDirective, FmxErrorsModule, FmxErrorsService, FmxFormSubmitDirective, FmxFormUtils, FmxSubmitModule, MaxLengthErrorResolver, MinLengthErrorResolver, RequiredErrorResolver };

//# sourceMappingURL=vixncu-ngx-formx.js.map